import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuthState } from './auth-state-context';

// Reactive replacement for "navigate on sign-out" living inside the sign-out
// button handler. Signing out (and, before this hook, an automatic session
// expiration) only cleared the auth store — nothing told the router to leave
// the authenticated screen, so the user was left staring at a dead screen
// until a reload or tab switch re-ran the route guards. Watching
// `session === null` here instead of calling `navigate()` from the button
// handler means every path that clears the session (manual sign-out, a
// failed refresh via `onSessionExpired`, or any future one) is covered by a
// single reactive check.
export interface UseRedirectWhenSignedOutOptions {
  /**
   * When true, never navigate even if `session === null` — used by the
   * offline-shell branch (task 3.6 / `_authenticated.tsx`), which has no
   * in-memory session by design and must not be bounced to a /login the
   * device may not even be able to reach.
   */
  skip?: boolean;
}

export function useRedirectWhenSignedOut(options: UseRedirectWhenSignedOutOptions = {}): void {
  const { skip = false } = options;
  const { session } = useAuthState();
  const navigate = useNavigate();

  useEffect(() => {
    if (session === null && !skip) {
      void navigate({ to: '/login' });
    }
  }, [session, skip, navigate]);
}
